import { Ref, useCallback, useMemo } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { useTranslation } from "@coworker/locales";
import { FixaSegmentedControl } from "@coworker/apprestructured/src/shared/wrappers/FixaSegmentedControl/FixaSegmentedControl";
import styles from "./SegmentedControlCreatorFragment.module.css";

type SegmentedControlItem = {
  value: string;
  label: string;
};

export type SegmentedControlCreatorFragmentConfigProps = {
  field: string;
  items: SegmentedControlItem[];
};

type SegmentedControlCreatorFragmentProps = {
  configProps: SegmentedControlCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function SegmentedControlCreatorFragment({
  configProps,
  commonProps,
}: SegmentedControlCreatorFragmentProps) {
  const { field, items } = configProps;
  const { blockInput, task, onTaskChange } = commonProps;

  const { t } = useTranslation();

  const selectedValue = (getByPath(task, field) as { value?: string } | null)
    ?.value;

  const selectedIndex = Math.max(
    items.findIndex((item) => item.value === selectedValue),
    0
  );

  const labels = useMemo<{ label: string; type: "button" }[]>(
    () => items.map((item) => ({ label: t(item.label), type: "button" })),
    [items, t]
  );

  const handleItemClick = useCallback(
    (_: unknown, i: number) => {
      onTaskChange(setByPath(field, items[i]));
    },
    [field, items, onTaskChange]
  );

  return (
    <div className={styles["root"]}>
      <FixaSegmentedControl
        items={labels}
        activeIndex={selectedIndex}
        size="xsmall"
        onClick={handleItemClick}
        disabled={blockInput}
      />
    </div>
  );
}

export default SegmentedControlCreatorFragment;
